import { ArrowDropDown, ArrowDropUp, CloseOutlined } from "@mui/icons-material";
import type { PaletteOptions, Theme } from "@mui/material/styles";
import { alpha, createTheme, ThemeOptions } from "@mui/material/styles";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import React from "react";

export type ChipTone = "primary" | "positive" | "negative" | "neutral" | "category" | "warning";

interface ChipToneColors {
  background: string;
  border: string;
  text: string;
}

declare module "@mui/material/styles" {
  interface Theme {
    chipTones: Record<ChipTone, ChipToneColors>;
  }

  interface ThemeOptions {
    chipTones?: Record<ChipTone, ChipToneColors>;
  }
}

// Define palette
const palette: PaletteOptions = {
  primary: {
    main: "#1A75FF",
    dark: "#1565c0",
    light: "#42a5f5",
    contrastText: "#fff",
  },
  secondary: {
    main: "#9c27b0",
    dark: "#7b1fa2",
    light: "#ba68c8",
    contrastText: "#fff",
  },
  text: {
    primary: "#011638",
    secondary: "#003180",
  },
  background: {
    default: "#0066ff08",
    paper: "#FFFFFF",
  },
  divider: "#E3E8EF",
};

const chipTones: Record<ChipTone, ChipToneColors> = {
  primary: {
    background: "#EFF6FF",
    border: "#BFDBFE",
    text: "#1D4ED8",
  },
  positive: {
    background: "#F0FDF4",
    border: "#A8D5B5",
    text: "#15803D",
  },
  warning: {
    background: "#FFFBEB",
    border: "#FDE68A",
    text: "#B45309",
  },
  negative: {
    background: "#FEF2F2",
    border: "#FECACA",
    text: "#B91C1C",
  },
  neutral: {
    background: "#F8FAFC",
    border: "#CBD5E1",
    text: "#475569",
  },
  category: {
    background: "#F1F5FB",
    border: "#D9E2F0",
    text: "#6478A9",
  },
};

type StatusChipColor = "success" | "warning" | "error";

const softStatusChipStyle =
  (color: StatusChipColor) =>
  ({ theme }: { theme: Theme }) => {
    const paletteColor = theme.palette[color];
    return {
      backgroundColor: alpha(paletteColor.main, 0.1),
      border: `1px solid ${alpha(paletteColor.main, 0.3)}`,
      color: paletteColor.dark,
      "& .MuiChip-icon, & .MuiChip-deleteIcon": { color: paletteColor.dark },
    };
  };

const SortDirectionIcon = ({ className }: SvgIconProps) =>
  React.createElement(
    "span",
    { "aria-hidden": true, className },
    React.createElement(ArrowDropUp, { className: "SortDirectionIcon-up" }),
    React.createElement(ArrowDropDown, { className: "SortDirectionIcon-down" }),
  );

const baseZIndex = createTheme().zIndex;

// Create theme configuration
const themeConfig: ThemeOptions = {
  palette,
  chipTones,
  // Keep the Global Header above navigation drawers and below every modal.
  // Dialogs, menus, autocomplete poppers and calendars share MUI's modal layer;
  // later portaled child controls stack above their parent in document order.
  zIndex: { ...baseZIndex, appBar: baseZIndex.drawer + 1 },
  direction: "ltr", // Default direction
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: "2.25rem",
    },
    h2: {
      fontWeight: 600,
      fontSize: "1.875rem",
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.5rem",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.25rem",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.125rem",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
    },
    body1: {
      fontSize: "1rem",
    },
    body2: {
      fontSize: "0.875rem",
    },
  },
  components: {
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "@media (pointer: coarse)": {
            minHeight: 44,
            minWidth: 44,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "8px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        // Input text in TextField/Autocomplete
        input: {
          fontSize: "16px",
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down("md")]: {
            width: "100%",
            minWidth: 0,
            overflow: "visible",
            position: "relative",
            marginBlockStart: theme.spacing(2),
            "&&": { paddingBlockStart: theme.spacing(2) },
            borderBlockStart: `1px solid ${theme.palette.divider}`,
            borderBlockEnd: 0,
            "&::before": {
              // Extend through the shared 16px mobile panel padding without moving controls.
              content: '""',
              position: "absolute",
              pointerEvents: "none",
              insetInline: theme.spacing(-2),
              top: -1,
              borderBlockStart: `1px solid ${theme.palette.divider}`,
            },
            '[data-pagination-region="top"] &, &[data-pagination-position="top"]': {
              marginBlockStart: 0,
              paddingBlockStart: 0,
              borderBlockStart: 0,
              borderBlockEnd: `1px solid ${theme.palette.divider}`,
              "&::before": { top: "auto", bottom: -1 },
            },
            "& .MuiTablePagination-toolbar": {
              display: "grid",
              gridTemplateColumns: "auto minmax(0, 1fr) auto",
              gridTemplateAreas: '"select count actions"',
              gap: theme.spacing(0.5),
              paddingInline: 0,
              paddingBlock: theme.spacing(0.5),
              minWidth: 0,
              width: "100%",
            },
            "& .MuiTablePagination-spacer": { display: "none" },
            "& .MuiTablePagination-selectLabel": {
              // Keep the localized accessible name while omitting the visible label.
              position: "absolute",
              width: 1,
              height: 1,
              overflow: "hidden",
              clipPath: "inset(50%)",
              whiteSpace: "nowrap",
            },
            "& .MuiTablePagination-input": {
              gridArea: "select",
              margin: 0,
              paddingInlineEnd: theme.spacing(2),
              marginInlineEnd: theme.spacing(1.5),
              borderInlineEnd: `1px solid ${theme.palette.divider}`,
            },
            "& .MuiTablePagination-selectIcon": {
              insetInlineEnd: theme.spacing(2),
            },
            "& .MuiTablePagination-displayedRows": {
              gridArea: "count",
              margin: 0,
              whiteSpace: "normal",
              textAlign: "end",
              fontSize: "0.8125rem",
            },
            "&& .MuiTablePagination-actions": {
              gridArea: "actions",
              margin: 0,
              display: "flex",
            },
          },
        }),
        selectRoot: {
          fontSize: "0.875rem",
        },
        select: {
          fontSize: "0.875rem",
        },
        menuItem: {
          fontSize: "0.875rem",
        },
      },
    },
    MuiTableSortLabel: {
      defaultProps: {
        IconComponent: SortDirectionIcon,
      },
      styleOverrides: {
        icon: ({ theme }) => ({
          display: "inline-flex",
          flexDirection: "column",
          // Keep the paired direction arrows visible without letting their
          // SVG view boxes determine the header column's minimum width.
          height: 32,
          justifyContent: "center",
          insetInlineStart: `calc(100% - ${theme.spacing(3)})`,
          opacity: "1 !important",
          position: "absolute",
          transform: "translateY(1px) !important",
          width: 24,
          "& .SortDirectionIcon-up, & .SortDirectionIcon-down": {
            color: theme.palette.action.disabled,
            display: "block",
            height: 24,
            opacity: 0.5,
            width: 24,
          },
          "& .SortDirectionIcon-up": {
            marginBlockEnd: -16,
          },
        }),
        root: ({ theme }) => ({
          paddingInlineEnd: theme.spacing(3),
          position: "relative",
          ".MuiTableCell-alignRight &": {
            paddingInlineEnd: 0,
            paddingInlineStart: theme.spacing(3),
          },
          ".MuiTableCell-alignRight & .MuiTableSortLabel-icon": {
            insetInlineEnd: `calc(100% - ${theme.spacing(3)})`,
            insetInlineStart: "auto",
          },
          "&:hover .SortDirectionIcon-up, &:hover .SortDirectionIcon-down, &:focus-visible .SortDirectionIcon-up, &:focus-visible .SortDirectionIcon-down":
            {
              opacity: 1,
            },
          "&.Mui-active.MuiTableSortLabel-directionAsc .SortDirectionIcon-up, &.Mui-active.MuiTableSortLabel-directionDesc .SortDirectionIcon-down":
            {
              color: theme.palette.text.primary,
              opacity: 1,
            },
        }),
      },
    },
    MuiPopper: {
      styleOverrides: {
        // Custom autocomplete slots use the base Popper rather than MUI's styled one.
        root: ({ theme }) => ({ zIndex: theme.zIndex.modal }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        // Dropdown menu text (paper content)
        paper: {
          fontSize: "16px",
        },
        // Option text size
        listbox: {
          "& .MuiAutocomplete-option": {
            fontSize: "14px",
          },
        },
      },
    },
    MuiChip: {
      defaultProps: {
        // Use a lighter, non-filled X icon for chip deletion by default
        deleteIcon: React.createElement(CloseOutlined, { fontSize: "small" }),
      },
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
        sizeSmall: {
          height: 22,
        },
      },
      variants: [
        {
          props: { color: "default" },
          style: {
            backgroundColor: "rgba(26, 117, 255, 0.08)",
            color: "#1A75FF",
            border: "1px solid rgba(26, 117, 255, 0.16)",
            "& .MuiChip-icon": { color: "#1A75FF" },
            "& .MuiChip-deleteIcon": { color: "#1A75FF" },
          },
        },
        {
          props: { color: "default", variant: "outlined" },
          style: {
            backgroundColor: "transparent",
            borderColor: "rgba(26, 117, 255, 0.32)",
          },
        },
        {
          props: { color: "success" },
          style: softStatusChipStyle("success"),
        },
        {
          props: { color: "warning" },
          style: softStatusChipStyle("warning"),
        },
        {
          props: { color: "error" },
          style: softStatusChipStyle("error"),
        },
      ],
    },
  },
};
// Function to create RTL theme
export const createRTLTheme = () => {
  return createTheme({
    ...themeConfig,
    direction: "rtl",
  });
};

// Function to create LTR theme
export const createLTRTheme = () => {
  return createTheme({
    ...themeConfig,
    direction: "ltr",
  });
};
